import Store from "../Redux/store";
import { showErrorToast, showWarningToast } from "../theme/create_toast/GenericToaster";
import InstanceMapper from "./InstanceMapper";
import { startApiLoading, endApiLoading } from "./apiLoadingManager";
import {
  extractServerErrorText,
  getUserFacingErrorMessage,
} from "./userFacingError";
import {
  isAuthzAppAccessDenied,
  isAuthzAppType,
} from "./authzPermissions";

/** Block business APIs when UMS denied this app (allow auth/me only). */
function assertAuthzAllowsApi(endpoint) {
  const state = Store.getState();
  const appType = state.globalState?.appType;
  if (!isAuthzAppType(appType)) return;
  if (!isAuthzAppAccessDenied(appType, state.authzState)) return;

  const path = String(endpoint || "");
  if (path.includes("/UserManagement/auth/me") || path.includes("/auth/me")) {
    return;
  }

  const error = new Error("Access denied for this application");
  error.status = 403;
  error.silent = true;
  throw error;
}

function resolveLoadingContext(method, endpoint, explicitContext) {
  if (explicitContext) return explicitContext;

  const path = (endpoint || "").split("?")[0];
  const httpMethod = (method || "GET").toUpperCase();

  if (httpMethod === "POST" && path.includes("/customers")) return "Creating customer";
  if (httpMethod === "PUT" && /\/customers\//.test(path)) return "Updating customer";
  if (httpMethod === "DELETE" && path.includes("/customers")) return "Deleting customer";

  if (httpMethod === "POST" && path.includes("/products")) return "Saving product";
  if (httpMethod === "PUT" && /\/products\//.test(path)) return "Updating product";
  if (httpMethod === "DELETE" && path.includes("/products")) return "Deleting product";

  if (httpMethod === "POST" && path.includes("/orders")) return "Saving order";
  if (httpMethod === "PUT" && /\/orders\//.test(path)) return "Updating order";
  if (httpMethod === "POST" && /\/orders\/.*\/generateInvoice/.test(path)) return "Generating invoice";
  if (httpMethod === "POST" && /\/orders\/.*\/deliveries\/.*\/generateInvoice/.test(path)) {
    return "Generating delivery invoice";
  }

  if (httpMethod === "POST" && path.includes("/categories")) return "Saving category";
  if (httpMethod === "PUT" && /\/categories\//.test(path)) return "Updating category";

  if (httpMethod === "POST" && path.includes("/VendorManagement")) return "Saving vendor";
  if (httpMethod === "PUT" && path.includes("/VendorManagement")) return "Updating vendor";

  if (httpMethod === "POST" && path.includes("/inventory")) return "Updating inventory";
  if (httpMethod === "POST" && path.includes("/import")) return "Importing data";

  if (httpMethod === "POST" && /\/invoices\/.*\/payments/.test(path)) return "Recording Invoice Payment";
  if (httpMethod === "POST" && /\/invoices\/.*\/payment-state/.test(path)) return "Syncing Invoice Payment";

  const pathLabels = {
    "/customers": "Loading customers",
    "/products": "Loading products",
    "/orders": "Loading orders",
    "/categories": "Loading categories",
    "/inventory": "Loading inventory",
    "/VendorManagement": "Loading vendors",
    "/invoices": "Loading invoices",
    "/quotations": "Loading quotations",
    "/deliveryNotes": "Loading deliveries",
    "/deliveryNote": "Loading delivery",
  };

  for (const [key, label] of Object.entries(pathLabels)) {
    if (path.includes(key)) return label;
  }

  if (httpMethod === "POST" || httpMethod === "PUT" || httpMethod === "PATCH" || httpMethod === "DELETE") {
    return "Please wait...";
  }

  return "Please wait...";
}

/**
 * Centralized API Client
 * 
 * Provides a standardized way to make API requests with automatic
 * basePath and Authorization token injection from Redux.
 */
const apiClient = {
  /**
   * Helper to get current config from Redux
   */
  getConfig() {
    const state = Store.getState();
    return {
      basePath: state.globalState?.basePath || "",
      gatewayUrl: state.globalState?.gatewayUrl || "",
      token: state.usersState?.token || "",
    };
  },

  /**
   * Core request method
   */
  async request(endpoint, options = {}) {
    assertAuthzAllowsApi(endpoint);

    const state = Store.getState();
    const { basePath, gatewayUrl, token } = this.getConfig();

    // Determine the full URL
    let url = endpoint;
    if (!endpoint.startsWith("http")) {
      const activeBase = options.useGateway ? gatewayUrl : basePath;
      const cleanBasePath = activeBase.endsWith("/") ? activeBase.slice(0, -1) : activeBase;
      const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

      // If endpoint already contains part of the basePath, don't re-prefix
      const companyPart = state.globalState?.companyName;
      if (!options.useGateway && companyPart && endpoint.includes(`/${companyPart}/`)) {
        if (!endpoint.startsWith("http")) {
          const host = state.globalState?.ip || "";
          const cleanHost = host.endsWith("/") ? host.slice(0, -1) : host;
          url = `${cleanHost}${cleanEndpoint}`;
        }
      } else {
        // --- DYNAMIC INSTANCE SWITCHING FOR HRMS ---
        const currentAppType = state.globalState?.appType || "";
        let finalBasePath = cleanBasePath;

        if (currentAppType.startsWith("HRMS") && !options.useGateway) {
          const requiredAppType = InstanceMapper.getAppTypeForEndpoint(endpoint);
          const purchasedApps = state.purchasedAppState?.purchasedApps || [];

          if (requiredAppType) {
            const targetApp = purchasedApps.find(app => app.PType === requiredAppType);

            if (targetApp && targetApp.PInstanceID) {
              const host = state.globalState?.ip || "";
              const cleanHost = host.endsWith("/") ? host.slice(0, -1) : host;
              finalBasePath = `${cleanHost}/${companyPart}/${targetApp.PInstanceID}`;
              console.log(`🔀 apiClient: Routing ${endpoint} to ${requiredAppType} instance: ${targetApp.PInstanceID}`);
            } else {
              // App not purchased
              const errorMsg = `First you have to purchase the ${requiredAppType} app, then you can access these reports.`;
              if (!options.silent) showWarningToast(errorMsg);
              const error = new Error(errorMsg);
              error.status = 403;
              error.context = options.context || "Access restricted";
              throw error;
            }
          }
        }

        url = `${finalBasePath}${cleanEndpoint}`;
      }
    }

    url = url.replace(/([^:]\/)\/+/g, "$1");

    // Append query params when callers pass options.params (fetch has no built-in params)
    if (options.params && typeof options.params === "object") {
      const search = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        search.append(key, String(value));
      });
      const qs = search.toString();
      if (qs) {
        url += (url.includes("?") ? "&" : "?") + qs;
      }
    }

    // Prepare headers
    const headers = {
      "Accept": "application/json",
      ...options.headers,
    };

    // Only set default Content-Type if not already specified and body is not FormData
    if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    // Inject token if available (skip for external URLs unless explicitly requested)
    const isExternalUrl = typeof endpoint === "string" && /^https?:\/\//i.test(endpoint);
    if (token && !options.skipToken && !isExternalUrl) {
      const cleanToken = Array.isArray(token) ? token[0] : token;
      if (cleanToken) {
        headers["Authorization"] = `Bearer ${cleanToken}`;
      }
    }

    // Diagnostic logging for development debugging
    if (process.env.NODE_ENV === 'development') {
      console.groupCollapsed(`🔍 API Request: ${endpoint}`);
      console.log('Context:', options.context || 'No context');
      console.log('URL:', url);

      const sanitizedHeaders = { ...headers };
      if (sanitizedHeaders.Authorization) {
        sanitizedHeaders.Authorization = `${sanitizedHeaders.Authorization.substring(0, 15)}... [TRUNCATED]`;
      }
      console.log('Headers:', sanitizedHeaders);
      console.groupEnd();
    }

    const config = {
      ...options,
      headers,
    };

    const loadingContext = options.hideLoader
      ? null
      : resolveLoadingContext(options.method || "GET", endpoint, options.context);

    if (loadingContext) {
      startApiLoading(loadingContext);
    }

    try {
      const response = await fetch(url, config);

      console.log(`📡 Response [${response.status}] for ${url}`);

      // Read response body ONCE - this is critical to avoid "body stream already read" error
      let rawData;
      let data;
      let parseError = null;

      try {
        const contentType = response.headers.get("content-type");

        if (contentType && contentType.includes("application/json")) {
          // Response claims to be JSON, try to parse
          data = await response.json();
          console.log(`📦 Response Data (JSON):`, data);
        } else {
          // Response is not JSON, read as text
          rawData = await response.text();
          console.log(`📝 Response Raw (Text, first 100 chars):`, rawData.substring(0, 100));

          // Try to parse as JSON if it looks like JSON
          if (rawData && (rawData.trim().startsWith('{') || rawData.trim().startsWith('['))) {
            try {
              data = JSON.parse(rawData);
            } catch (e) {
              // Keep as text if JSON parse fails
              console.warn('Response looks like JSON but failed to parse:', e);
              data = rawData;
            }
          } else {
            data = rawData;
          }
        }
      } catch (parseErr) {
        parseError = parseErr;

        // Suppress warning for common non-JSON responses (they're harmless)
        if (parseErr.message?.includes('Unexpected token') ||
          parseErr.message?.includes('Unexpected end of JSON input')) {
          console.debug('ℹ️ Response is not JSON (this is normal for some endpoints):', parseErr.message);
        } else {
          console.error('🚨 Failed to parse response:', parseErr);
        }

        // Use whatever we already read, or set a fallback
        data = rawData || 'Failed to read response';
      }

      if (!response.ok) {
        const serverText = extractServerErrorText(data);
        const error = new Error(serverText || response.statusText || "Request failed");
        error.status = response.status;
        error.data = data;
        error.url = url;
        error.context = options.context || "Operation";
        error.parseError = parseError;
        throw error;
      }

      // If response is OK but we had a parse error, only warn for unexpected errors
      if (parseError) {
        // Suppress for common non-JSON responses
        if (!parseError.message?.includes('Unexpected token') &&
          !parseError.message?.includes('Unexpected end of JSON input')) {
          console.warn('⚠️ Response parsed with errors but status was OK');
        }
      }

      return data;
    } catch (error) {
      const context = error.context || options.context || "Request";
      let actionHint = "default";
      const ctx = String(context).toLowerCase();
      if (ctx.includes("delivery status") || ctx.includes("ship")) actionHint = "delivery-status";
      else if (ctx.includes("voiding") && ctx.includes("payment")) actionHint = "void-payment";
      else if (ctx.includes("payment")) actionHint = "payment";
      else if (ctx.includes("delivery")) actionHint = "save-delivery";

      let userMessage = getUserFacingErrorMessage(error, { action: actionHint });

      if (error.status === 401) {
        userMessage = "Your session has expired. Please log in again.";
      } else if (error.status === 403 && !extractServerErrorText(error.data)) {
        userMessage = "You don't have permission to perform this action.";
      } else if (error.status === 431) {
        userMessage =
          "Request headers too large. Please clear browser cache/cookies or contact support.";
        console.error("🚨 431 ERROR - Request Header Fields Too Large");
      } else if (error.name === "TypeError" && error.message === "Failed to fetch") {
        userMessage = "Network error. Please check your connection and try again. Nothing was changed.";
      } else if (error.name === "SyntaxError" && String(error.message || "").includes("JSON")) {
        userMessage = "Server returned an invalid response. Please try again.";
        console.error("🚨 JSON Parse Error - Server returned:", error.data || "unknown");
      }

      error.userMessage = userMessage;
      error.message = userMessage;

      // One calm toast — callers should skip re-toasting when error.toastShown is true.
      if (!options.silent) {
        showErrorToast(userMessage);
        error.toastShown = true;
      }

      console.error(`[apiClient] ${context} error at ${url}:`, error);
      throw error;
    } finally {
      if (loadingContext) {
        endApiLoading();
      }
    }
  },

  // Convenience methods
  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: "GET" });
  },

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: "DELETE" });
  },

  /**
   * Post Multipart Data (FormData)
   * 
   * Special helper for file uploads that ensures the correct token
   * and tenant headers are added, while allowing the browser to
   * set the multipart boundary automatically.
   */
  postMultipart(endpoint, formData, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      body: formData,
    });
  },
};

export default apiClient;
